import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './FileUpload.css';
import ENV_CONFIG from '../config/environment';

const API_BASE_URL = ENV_CONFIG.getBaseUrl();

const FileUpload = () => {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [playBookUrl, setPlayBookUrl] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch uploaded files on component mount
  useEffect(() => {
    fetchCurrentUser();
    fetchFiles();
  }, []);

  const fetchCurrentUser = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('No authentication token found');
        setLoading(false);
        return;
      }
      
      const response = await axios.get(`${API_BASE_URL}/api/current-user/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCurrentUser(response.data);
      setError(null);
    } catch (error) {
      console.error('Error fetching current user:', error);
      setError('Failed to fetch user information');
      
      if (error.response?.status === 401) {
        localStorage.removeItem('token');
        // Redirect to login if needed
        // window.location.href = '/login';
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchFiles = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('No authentication token found');
        return;
      }

      const response = await axios.get(`${API_BASE_URL}/api/files/all/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setFiles(response.data);
      setError(null);
    } catch (error) {
      console.error('Error fetching files:', error);
      
      if (error.response?.status === 401) {
        setError('Authentication failed. Please log in again.');
        localStorage.removeItem('token');
      } else if (error.response?.status === 404) {
        setError('Files endpoint not found. Please check your API configuration.');
      } else {
        setError('Failed to fetch files. Please try again.');
      }
    }
  };

  const handleFileUpload = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('file_type', 'mp4');

    try {
      setUploading(true);
      setError(null);
      
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_BASE_URL}/api/files/upload/`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      // Refresh the file list to show the new file
      await fetchFiles();
      alert('File uploaded successfully!');
    } catch (error) {
      console.error('Error uploading file:', error);
      
      let errorMessage = 'Error uploading file. Please try again.';
      
      if (error.response?.status === 413) {
        errorMessage = 'File is too large. Please try a smaller file.';
      } else if (error.response?.status === 401) {
        errorMessage = 'Session expired. Please log in again.';
        localStorage.removeItem('token');
      } else if (error.response?.status === 400) {
        errorMessage = error.response.data?.detail || 'Invalid file data. Please check your file.';
      }
      
      alert(errorMessage);
      setError(errorMessage);
    } finally {
      setUploading(false);
    }
  };

  const handlePlayBookUpload = async () => {
    if (!playBookUrl.trim()) {
      alert('Please enter a valid Playbook URL');
      return;
    }

    // Basic URL validation
    try {
      new URL(playBookUrl);
    } catch {
      alert('Please enter a valid URL');
      return;
    }

    try {
      setUploading(true);
      setError(null);
      
      const token = localStorage.getItem('token');
      await axios.post(`${API_BASE_URL}/api/files/upload/`, {
        url: playBookUrl,
        file_type: 'playbook'
      }, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      setPlayBookUrl('');
      await fetchFiles(); // Refresh the file list
      alert('Playbook URL added successfully!');
    } catch (error) {
      console.error('Error adding playbook URL:', error);
      
      let errorMessage = 'Error adding playbook URL. Please try again.';
      
      if (error.response?.status === 401) {
        errorMessage = 'Session expired. Please log in again.';
        localStorage.removeItem('token');
      } else if (error.response?.status === 400) {
        errorMessage = error.response.data?.detail || 'Invalid URL. Please check your Playbook URL.';
      }
      
      alert(errorMessage);
      setError(errorMessage);
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteFile = async (fileId, uploadedBy) => {
    // Check if user can delete this file
    const canDelete = currentUser && (
      currentUser.id === uploadedBy || 
      currentUser.is_superuser || 
      currentUser.role === 'superuser' ||
      currentUser.role === 'admin'
    );

    if (!canDelete) {
      alert('You can only delete your own files unless you are an admin.');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this file? This action cannot be undone.')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE_URL}/api/files/${fileId}/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Refresh the file list after successful deletion
      await fetchFiles();
      alert('File deleted successfully!');
    } catch (error) {
      console.error('Error deleting file:', error);
      
      let errorMessage = 'Error deleting file. Please try again.';
      
      if (error.response?.status === 403) {
        errorMessage = 'Permission denied. You can only delete your own files.';
      } else if (error.response?.status === 404) {
        errorMessage = 'File not found. It may have already been deleted.';
      } else if (error.response?.status === 401) {
        errorMessage = 'Session expired. Please log in again.';
        localStorage.removeItem('token');
      }
      
      alert(errorMessage);
    }
  };

  const handleViewFile = (file) => {
    setSelectedFile(file);
    setShowModal(true);
  };

  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const canDeleteFile = (uploadedBy) => {
    return currentUser && (
      currentUser.id === uploadedBy || 
      currentUser.is_superuser || 
      currentUser.role === 'superuser' ||
      currentUser.role === 'admin'
    );
  };

  const isOwner = (uploadedBy) => {
    return currentUser && currentUser.id === uploadedBy;
  };

  if (loading) {
    return (
      <div className="file-upload-container">
        <div className="loading">Loading...</div>
      </div>
    );
  }

  return (
    <div className="file-upload-container">
      {error && (
        <div className="error-message" style={{
          backgroundColor: '#fee',
          color: '#c33',
          padding: '10px',
          borderRadius: '4px',
          marginBottom: '20px',
          border: '1px solid #fcc'
        }}>
          {error}
          <button 
            onClick={() => setError(null)}
            style={{ float: 'right', background: 'none', border: 'none', fontSize: '16px', cursor: 'pointer' }}
          >
            ×
          </button>
        </div>
      )}

      <div className="upload-section">
        <h3>Upload Files</h3>
        
        {/* User Info */}
        {currentUser && (
          <div className="user-info" style={{ marginBottom: '20px', padding: '10px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
            <span>Logged in as: <strong>{currentUser.username}</strong></span>
            {currentUser.is_superuser && <span style={{ marginLeft: '10px', color: '#007bff' }}>(Admin)</span>}
          </div>
        )}
        
        {/* MP4 File Upload */}
        <div className="upload-option">
          <h4>Upload MP4 File</h4>
          <input
            type="file"
            accept=".mp4,video/mp4"
            onChange={(e) => {
              const file = e.target.files[0];
              if (file) {
                // Check file type
                if (!file.type.includes('mp4') && !file.name.toLowerCase().endsWith('.mp4')) {
                  alert('Please select only MP4 files');
                  e.target.value = '';
                  return;
                }
                
                // Check file size (e.g., max 100MB)
                const maxSize = 100 * 1024 * 1024; // 100MB
                if (file.size > maxSize) {
                  alert('File is too large. Maximum size is 100MB.');
                  e.target.value = '';
                  return;
                }
                
                handleFileUpload(file);
                e.target.value = ''; // Reset input after upload
              }
            }}
            disabled={uploading}
            className="file-input"
          />
          <small className="file-hint">Maximum file size: 100MB</small>
        </div>

        {/* Playbook URL Upload */}
        <div className="upload-option">
          <h4>Add Playbook URL</h4>
          <div className="url-input-container">
            <input
              type="url"
              placeholder="https://www.playbook.com/s/..."
              value={playBookUrl}
              onChange={(e) => setPlayBookUrl(e.target.value)}
              disabled={uploading}
              className="url-input"
            />
            <button
              onClick={handlePlayBookUpload}
              disabled={uploading || !playBookUrl.trim()}
              className="upload-btn"
            >
              {uploading ? 'Adding...' : 'Add URL'}
            </button>
          </div>
        </div>
      </div>

      {/* File List */}
      <div className="file-list-section">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h3>All Uploaded Files ({files.length})</h3>
          <button 
            onClick={fetchFiles}
            className="refresh-btn"
            style={{ 
              padding: '8px 16px', 
              backgroundColor: '#007bff', 
              color: 'white', 
              border: 'none', 
              borderRadius: '4px', 
              cursor: 'pointer' 
            }}
          >
            Refresh
          </button>
        </div>
        
        {files.length === 0 ? (
          <div className="no-files">
            <p>No files uploaded yet</p>
          </div>
        ) : (
          <div className="file-list">
            {files.map((file) => (
              <div key={file.id} className={`file-item ${isOwner(file.uploaded_by) ? 'owned-file' : ''}`}>
                <div className="file-info">
                  <div className="file-icon">
                    {file.file_type === 'mp4' ? '🎥' : '📋'}
                  </div>
                  <div className="file-details">
                    <div className="file-name">
                      {file.name}
                      {isOwner(file.uploaded_by) && <span className="owner-badge">Your file</span>}
                    </div>
                    <div className="file-meta">
                      <span className="file-type">{file.file_type.toUpperCase()}</span>
                      {file.size && <span className="file-size"> • {formatFileSize(file.size)}</span>}
                      <span className="file-date"> • {formatDate(file.created_at)}</span>
                      {file.uploaded_by_username && (
                        <span className="file-uploader"> • By {file.uploaded_by_username}</span>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="file-actions">
                  <button
                    onClick={() => handleViewFile(file)}
                    className="view-btn"
                    title="View file"
                  >
                    👁️ View
                  </button>
                  {canDeleteFile(file.uploaded_by) && (
                    <button
                      onClick={() => handleDeleteFile(file.id, file.uploaded_by)}
                      className="delete-btn"
                      title={isOwner(file.uploaded_by) ? "Delete your file" : "Delete file (Admin)"}
                    >
                      🗑️ Delete
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Full Screen Modal */}
      {showModal && selectedFile && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{selectedFile.name}</h3>
              <button
                onClick={() => setShowModal(false)}
                className="close-btn"
                title="Close"
              >
                ✕
              </button>
            </div>
            
            <div className="modal-body">
              {selectedFile.file_type === 'mp4' ? (
                <video
                  controls
                  className="modal-video"
                  src={selectedFile.file_url || `${API_BASE_URL}${selectedFile.file_path}`}
                  onError={(e) => {
                    console.error('Video loading error:', e);
                    alert('Error loading video. The file may be corrupted or moved.');
                  }}
                >
                  Your browser does not support the video tag.
                </video>
              ) : (
                <iframe
                  src={selectedFile.url}
                  className="modal-iframe"
                  title={selectedFile.name}
                  frameBorder="0"
                  allowFullScreen
                  onError={() => {
                    console.error('Iframe loading error');
                    alert('Error loading content. The URL may be invalid or inaccessible.');
                  }}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FileUpload;